import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuthContext } from '../../context/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import { sendPasswordResetEmail } from 'firebase/auth';
import { toast } from 'react-toastify';
import Link from '@mui/material/Link';

const Login = () => {
  const { signInWithGoogle, signInWithEmailAndPassword, signingIn, currentUser } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);

  // Pre-fill email from URL params and show message if provided
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const messageParam = searchParams.get('message');
    const providerParam = searchParams.get('authProvider');

    if (emailParam) {
      setEmail(emailParam);
    }

    if (messageParam) {
      setInfoMessage(decodeURIComponent(messageParam));
    }

    if (providerParam) {
      setAuthProvider(providerParam);
    }
  }, [searchParams]);

  // Redirect if already logged in
  if (currentUser) {
    return <Navigate to="/employees" replace />;
  }

  const handleEmailLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password && password.length >= 8) {
      signInWithEmailAndPassword(email, password);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      toast.error('Please enter your email address first');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setResettingPassword(true);
      await sendPasswordResetEmail(auth, email);
      setPasswordResetSent(true);
      toast.success('Password reset email sent! Please check your inbox.');
    } catch (err: any) {
      console.error('Error sending password reset:', err);
      if (err.code === 'auth/user-not-found') {
        toast.error('No account found with this email address');
      } else if (err.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else {
        toast.error('Failed to send password reset email. Please try again.');
      }
    } finally {
      setResettingPassword(false);
    }
  };

  // Form validation: disable login button until email and 8+ char password
  const isFormValid = email.length > 0 && password.length >= 8;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
        py: 4
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 4,
            boxShadow: '0px 10px 40px rgba(0,0,0,0.08)'
          }}
        >
          {/* Brand Logo / Title */}
          <Box sx={{ mb: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Image Logo */}
            <Box
              component="img"
              src={require('../../assets/logoLarge.png')}
              alt="CycleSaver Logo"
              sx={{
                height: 'auto',
                width: 'auto',
                maxHeight: '80px',
                maxWidth: '80%',
                mb: 2,
                objectFit: 'contain'
              }}
            />

            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mt: 1, fontWeight: 500 }}
            >
              Employer Portal
            </Typography>
          </Box>

          {/* Info message if redirected from register */}
          {infoMessage && (
            <Alert severity="info" sx={{ width: '100%', mb: 3 }}>
              {infoMessage}
            </Alert>
          )}

          {/* Only show Google button if authProvider is 'google' or not specified (new users) */}
          {(authProvider === 'google' || authProvider === null) && (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={signInWithGoogle}
                disabled={signingIn}
                sx={{
                  mb: 3,
                  py: 1.5,
                  borderColor: '#e0e0e0',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: '#bdbdbd',
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                Continue with Google
              </Button>

              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 3 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mx: 2 }}>
                  OR
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            </>
          )}

          <Box component="form" onSubmit={handleEmailLogin} noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={signingIn}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2.5 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={signingIn}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 4 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={signingIn || !isFormValid}
              sx={{ py: 1.8, fontSize: '1rem' }}
            >
              {signingIn ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>

            {/* Password Reset Link */}
            <Box sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
              {passwordResetSent ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Password reset email sent! Check your inbox.
                </Alert>
              ) : (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handlePasswordReset}
                  disabled={resettingPassword || !email}
                  sx={{
                    cursor: (resettingPassword || !email) ? 'not-allowed' : 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {resettingPassword ? 'Sending...' : 'Forgot your password?'}
                </Link>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;